/**
 * Manages filter and sorting state persistence via localStorage and URL
 */

interface FilterState {
  filters: Array<{
    id: string;
    operator: string;
    value: string;
    enabled: boolean;
  }>;
  sorting: Array<{
    id: string;
    direction: 'asc' | 'desc';
    enabled: boolean;
  }>;
}

const STORAGE_PREFIX = 'filterState';

/**
 * Generate a storage key based on route pattern
 */
function getStorageKey(pathname: string): string {
  // Normalize path to a storage key
  // Examples: "/" -> "root", "/upcoming/theaters" -> "upcoming", "/u/:username/ratings" -> "userRatings"
  if (pathname === '/') {
    return `${STORAGE_PREFIX}:root`;
  }

  const segments = pathname.split('/').filter(Boolean);
  const pageType = segments[0] || 'root';

  return `${STORAGE_PREFIX}:${pageType}`;
}

/**
 * Save filter state to localStorage
 */
export function saveToLocalStorage(pathname: string, state: FilterState): void {
  const key = getStorageKey(pathname);
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save filter state to localStorage:', error);
  }
}

/**
 * Load filter state from localStorage
 */
export function loadFromLocalStorage(pathname: string): FilterState | null {
  const key = getStorageKey(pathname);
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load filter state from localStorage:', error);
    return null;
  }
}

/**
 * Clear filter state from localStorage
 */
export function clearFromLocalStorage(pathname: string): void {
  const key = getStorageKey(pathname);
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear filter state from localStorage:', error);
  }
}

/**
 * Encode filter state to URL query parameters
 * Format: ?f=fieldId:operator:value|fieldId:operator:value&s=fieldId:direction|fieldId:direction
 */
export function encodeToURL(state: FilterState): Record<string, string> {
  const params: Record<string, string> = {};

  // Encode filters
  if (state.filters.length > 0) {
    const enabledFilters = state.filters.filter((f) => f.enabled);
    if (enabledFilters.length > 0) {
      params.f = enabledFilters
        .map((f) => `${f.id}:${f.operator}:${encodeURIComponent(f.value)}`)
        .join('|');
    }
  }

  // Encode sortings
  if (state.sorting.length > 0) {
    const enabledSortings = state.sorting.filter((s) => s.enabled);
    if (enabledSortings.length > 0) {
      params.s = enabledSortings.map((s) => `${s.id}:${s.direction}`).join('|');
    }
  }

  return params;
}

/**
 * Decode filter state from URL query parameters
 */
export function decodeFromURL(
  searchParams: URLSearchParams,
): FilterState | null {
  const filterParam = searchParams.get('f');
  const sortParam = searchParams.get('s');

  if (!filterParam && !sortParam) {
    return null;
  }

  const state: FilterState = {
    filters: [],
    sorting: [],
  };

  // Decode filters
  if (filterParam) {
    state.filters = filterParam.split('|').map((f) => {
      const parts = f.split(':');
      const value = parts.slice(2).join(':'); // Handle colons in value
      return {
        id: parts[0],
        operator: parts[1],
        value: decodeURIComponent(value),
        enabled: true,
      };
    });
  }

  // Decode sortings
  if (sortParam) {
    state.sorting = sortParam.split('|').map((s) => {
      const parts = s.split(':');
      return {
        id: parts[0],
        direction: (parts[1] as 'asc' | 'desc') || 'desc',
        enabled: true,
      };
    });
  }

  return state;
}

/**
 * Merge two filter states, with priority state taking precedence
 */
export function mergeFilterStates(
  defaultState: FilterState,
  overrideState: FilterState | null,
): FilterState {
  if (!overrideState) {
    return defaultState;
  }

  return {
    filters:
      overrideState.filters.length > 0
        ? overrideState.filters
        : defaultState.filters,
    sorting:
      overrideState.sorting.length > 0
        ? overrideState.sorting
        : defaultState.sorting,
  };
}
